// pos_local sinxronlaşma vəziyyətini izləyən hook.
//
// POLL SÜRƏTİ DƏYİŞKƏNDİR: sinxronlaşma gedərkən tez (1.5s) — istifadəçi
// tərəqqinin hərəkət etdiyini görməlidir; sakit vaxtda seyrək (15s) — POS
// gün boyu açıq qalır və hər saniyə sorğu atmaq mənasız yükdür.

import { useCallback, useEffect, useRef, useState } from 'react';

import { POS_ENABLED } from '../api/pos/config';
import {
  appliedCount,
  getSyncStatus,
  initialProgress,
  isCloudLinked,
  isDeviceRevoked,
  isSyncing,
  pendingOutbox,
  syncError,
  type SyncStatus,
} from '../api/pos/syncStatus';

const ACTIVE_POLL_MS = 1500;
const IDLE_POLL_MS = 15000;

export interface SyncView {
  /** POS rejimi deyilsə (brauzer) heç nə göstərilmir. */
  enabled: boolean;
  status: SyncStatus | null;
  /** Hazırda məlumat çəkilir. */
  syncing: boolean;
  /** İlk tam sinxron hələ bitməyib — progress bar bunun üçündür. */
  initial: boolean;
  /** 0..1 */
  progress: number;
  /** Tətbiq olunmuş event sayı. */
  applied: number;
  /** Buluda göndərilməyi gözləyən hadisələr. */
  pending: number;
  /** Cihaz buluda qoşulub (qeydiyyatdan keçib). */
  linked: boolean;
  /** Son sinxron xətası — null deyilsə ekranda göstərilir. */
  error: string | null;
  /** Cihaz admin paneldə ləğv olunub — POS yenidən qeydiyyat istəyəcək. */
  revoked: boolean;
  refresh: () => void;
}

export function useSyncStatus(): SyncView {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  const tick = useCallback(async () => {
    let next: SyncStatus | null = null;
    try {
      next = await getSyncStatus();
    } catch {
      // pos_local hələ qalxmayıbsa və ya bağlanıbsa — sükutla keç.
      // Göstərici sadəcə köhnə vəziyyəti saxlayır, xəta göstərmir.
    }
    if (!mounted.current) return;
    if (next) setStatus(next);

    // Növbəti sorğunu YENİ vəziyyətə görə planla: sinxron gedirsə tez qayıt.
    const busy = next ? isSyncing(next) || next.pull.initial : false;
    timer.current = setTimeout(tick, busy ? ACTIVE_POLL_MS : IDLE_POLL_MS);
  }, []);

  useEffect(() => {
    if (!POS_ENABLED) return undefined;
    mounted.current = true;
    void tick();
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [tick]);

  const refresh = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    void tick();
  }, [tick]);

  return {
    enabled: POS_ENABLED,
    status,
    syncing: isSyncing(status),
    initial: status?.pull.initial ?? false,
    progress: initialProgress(status),
    applied: appliedCount(status),
    pending: pendingOutbox(status),
    linked: isCloudLinked(status),
    error: syncError(status),
    revoked: isDeviceRevoked(status),
    refresh,
  };
}
